import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Lock,
  Person,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store';
import { login, clearError, setError } from '../../store/slices/authSlice';
import hfssLogo from '../../assets/hfss_logo.png';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    // ONLY handle navigation if the modal is actually open
    if (!open) return;
    
    if (isAuthenticated && user) {
      onClose();
      
      if (onSuccess) {
        onSuccess();
        return;
      }

      // Handle pending club registration
      const pendingRegistration = localStorage.getItem('pendingClubRegistration');
      if (pendingRegistration) {
        try {
          const registrationData = JSON.parse(pendingRegistration);
          if (
            registrationData.email === user.email &&
            Date.now() - registrationData.timestamp < 60 * 60 * 1000
          ) {
            localStorage.removeItem('pendingClubRegistration');
            navigate('/registrations');
            return;
          } else {
            localStorage.removeItem('pendingClubRegistration');
          }
        } catch (e) {
          localStorage.removeItem('pendingClubRegistration');
        }
      }

      // Default redirect logic
      if (user.user_type === 'club_manager' || user.is_platform_admin) {
        navigate('/app/dashboard');
      } else if (user.is_pilot || user.user_type === 'pilot') {
        navigate('/app/pilot-dashboard');
      } else {
        // For basic users, let SmartRedirect decide
        navigate('/app');
      }
    }
  }, [open, isAuthenticated, user, navigate, onClose, onSuccess]);

  useEffect(() => {
    // Reset form when modal opens/closes
    if (!open) {
      setUsername('');
      setPassword('');
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      dispatch(setError(t('login.enterCredentials', 'Please enter your credentials')));
      return;
    }

    dispatch(login({ username, password }));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundImage: 'none',
          mx: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <Close />
        </IconButton>
        
        <DialogContent sx={{ pt: { xs: 3, sm: 4 }, pb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src={hfssLogo}
              alt="HFSS"
              style={{
                height: 60,
                marginBottom: 12,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            />
            <Typography
              component="h1"
              variant="h5"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5,
                fontSize: { xs: '1.4rem', sm: '1.5rem' },
              }}
            >
              {t('login.title', 'Clubs Portal')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t('login.subtitle', 'Sign in to access your account')}
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2, mb: 2 }}
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="dense"
              required
              fullWidth
              size="small"
              id="username"
              label={t('login.usernameOrEmailLabel', 'Email or Username')}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.usernameOrEmailPlaceholder', 'your@email.com or username')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                mb: 1.5,
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root': {
                  '& input': {
                    py: 1,
                    fontSize: '0.9rem',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3388ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3388ff',
                  },
                },
              }}
            />
            
            <TextField
              margin="dense"
              required
              fullWidth
              size="small"
              name="password"
              label={t('login.password', 'Password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                mb: 0,
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root': {
                  '& input': {
                    py: 1,
                    fontSize: '0.9rem',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3388ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3388ff',
                  },
                },
              }}
            />

            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
              <Button
                onClick={() => {
                  onClose();
                  navigate('/forgot-password');
                }}
                sx={{
                  color: '#3388ff',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  padding: '2px 4px',
                  '&:hover': {
                    color: '#0066ff',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {t('login.forgotPassword', 'Forgot password?')}
              </Button>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="medium"
              sx={{
                mt: 2,
                mb: 2,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                boxShadow: '0 2px 4px 1px rgba(51, 136, 255, .2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px 2px rgba(51, 136, 255, .25)',
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('login.signIn', 'Sign In')
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {t('login.needAccount', 'Need an account?')}
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0, sm: 0.5 },
                justifyContent: 'center',
                alignItems: 'center',
                mt: 0.5
              }}>
                <Button
                  onClick={() => {
                    onClose();
                    navigate('/registrations');
                  }}
                  sx={{
                    color: '#3388ff',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    padding: '2px 6px',
                    '&:hover': {
                      color: '#0066ff',
                      backgroundColor: 'rgba(51, 136, 255, 0.04)',
                    },
                  }}
                >
                  {t('login.registerPilot', 'Register as Pilot')}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ alignSelf: 'center', fontSize: '0.75rem', display: { xs: 'none', sm: 'block' } }}
                >
                  |
                </Typography>
                <Button
                  onClick={() => {
                    onClose();
                    navigate('/register');
                  }}
                  sx={{
                    color: '#764ba2',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    padding: '2px 6px',
                    '&:hover': {
                      color: '#5a3687',
                      backgroundColor: 'rgba(118, 75, 162, 0.04)',
                    },
                  }}
                >
                  {t('login.registerClubManager', 'Register as Club Manager')}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default LoginModal;